import React, { useState } from "react";
import Card from "components/card";
import {
  MdMenuBook,
  MdAdd,
  MdBusinessCenter,
  MdAssignment,
  MdPerson,
  MdSwapHoriz,
  MdKeyboardArrowDown,
  MdKeyboardArrowUp,
  MdGroups,
  MdInfoOutline,
  MdFolder,
  MdBarChart,
  MdReceiptLong,
  MdAttachMoney,
  MdCalendarToday,
  MdSettings
} from "react-icons/md";

const AccordionItem = ({ title, icon: Icon, children, isOpen, onClick }) => {
  return (
    <div className="mb-4 rounded-xl border border-gray-200 bg-white dark:border-white/10 dark:bg-navy-800">
      <button
        onClick={onClick}
        className="flex w-full items-center justify-between px-5 py-4 text-left transition-colors hover:bg-gray-50 dark:hover:bg-navy-700 rounded-xl"
      >
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-500/10 text-brand-500 dark:bg-brand-400/10 dark:text-brand-400">
            <Icon className="h-6 w-6" />
          </div>
          <h3 className="text-lg font-bold text-navy-700 dark:text-white">{title}</h3>
        </div>
        <div className="text-gray-500 dark:text-gray-400">
          {isOpen ? <MdKeyboardArrowUp className="h-6 w-6" /> : <MdKeyboardArrowDown className="h-6 w-6" />}
        </div>
      </button>
      {isOpen && (
        <div className="px-5 pb-5 pt-2">
          {children}
        </div>
      )}
    </div>
  );
};

const UserManual = () => {
  const [openSection, setOpenSection] = useState("overview");

  const toggleSection = (section) => {
    setOpenSection(openSection === section ? null : section);
  };

  return (
    <div className="mt-3">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-navy-700 dark:text-white">User Manual</h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            A comprehensive visual guide on how to use the CRM effectively.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-5">
        
        {/* OVERVIEW SECTION */}
        <AccordionItem
          title="System Overview"
          icon={MdMenuBook}
          isOpen={openSection === "overview"}
          onClick={() => toggleSection("overview")}
        >
          <div className="text-sm text-gray-600 dark:text-gray-300 space-y-4">
            <p>
              Welcome to your CRM! The system is built on three core pillars:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
              <Card extra="p-4 bg-gray-50 dark:bg-navy-700 border border-gray-100 dark:border-white/5">
                <div className="flex items-center gap-2 mb-2 text-brand-500">
                  <MdBusinessCenter className="h-5 w-5" />
                  <span className="font-bold text-navy-700 dark:text-white">1. CareOf</span>
                </div>
                <p className="text-xs">
                  A Parent account or Agency. A single CareOf can have multiple Leads or Clients under them.
                </p>
              </Card>
              <Card extra="p-4 bg-gray-50 dark:bg-navy-700 border border-gray-100 dark:border-white/5">
                <div className="flex items-center gap-2 mb-2 text-orange-500">
                  <MdAssignment className="h-5 w-5" />
                  <span className="font-bold text-navy-700 dark:text-white">2. Lead</span>
                </div>
                <p className="text-xs">
                  A potential customer or project. You track follow-ups, notes, and progress here.
                </p>
              </Card>
              <Card extra="p-4 bg-gray-50 dark:bg-navy-700 border border-gray-100 dark:border-white/5">
                <div className="flex items-center gap-2 mb-2 text-green-500">
                  <MdPerson className="h-5 w-5" />
                  <span className="font-bold text-navy-700 dark:text-white">3. Client</span>
                </div>
                <p className="text-xs">
                  A won project. Once a lead is successful, it converts to a client where you can generate receipts and track revenue.
                </p>
              </Card>
            </div>
          </div>
        </AccordionItem>

        {/* CAREOF MANAGEMENT */}
        <AccordionItem
          title="CareOf Management"
          icon={MdBusinessCenter}
          isOpen={openSection === "careof"}
          onClick={() => toggleSection("careof")}
        >
          <div className="text-sm text-gray-600 dark:text-gray-300 space-y-4">
            <p>
              CareOfs are usually agencies, companies, or partners that refer multiple leads to you. By creating a CareOf, you can easily track all associated leads in one place.
            </p>
            
            <h4 className="font-bold text-navy-700 dark:text-white mt-6 mb-2">How to Create a CareOf:</h4>
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-3 bg-gray-50 dark:bg-navy-700 p-3 rounded-lg border border-gray-100 dark:border-white/10">
                <div className="bg-brand-500 text-white rounded-lg p-2"><MdAdd /></div>
                <span>Navigate to <b>CareOf Management</b> from the sidebar and click the "Add CareOf" button.</span>
              </div>
              <div className="flex items-center gap-3 bg-gray-50 dark:bg-navy-700 p-3 rounded-lg border border-gray-100 dark:border-white/10">
                <div className="bg-gray-200 dark:bg-navy-600 rounded-lg p-2 text-gray-700 dark:text-white"><MdInfoOutline /></div>
                <span>Fill in their <b>Name, Phone, and Priority</b>. They will be added to your parent list.</span>
              </div>
            </div>

            <div className="mt-4 p-4 border border-blue-100 bg-blue-50 dark:border-blue-900 dark:bg-blue-900/20 rounded-xl">
              <p className="font-semibold text-blue-800 dark:text-blue-300 mb-2">Visual Interface Dummy:</p>
              <div className="flex items-center justify-between bg-white dark:bg-navy-800 p-3 rounded shadow-sm border border-gray-200 dark:border-white/10">
                <div className="flex flex-col">
                  <span className="font-bold text-navy-700 dark:text-white text-xs">GLOBAL MARKETING INC</span>
                  <span className="text-xs text-gray-500">Business • Active</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-700 font-bold dark:bg-red-900/40 dark:text-red-300">Urgent</span>
                  <button className="text-xs bg-brand-500 text-white px-3 py-1 rounded hover:bg-brand-600">View Profile</button>
                </div>
              </div>
            </div>
          </div>
        </AccordionItem>

        {/* LEAD MANAGEMENT */}
        <AccordionItem
          title="Lead Management"
          icon={MdAssignment}
          isOpen={openSection === "lead"}
          onClick={() => toggleSection("lead")}
        >
          <div className="text-sm text-gray-600 dark:text-gray-300 space-y-4">
            <p>
              Leads are the core of your sales pipeline. When a new prospect arrives, you register them here.
            </p>
            
            <h4 className="font-bold text-navy-700 dark:text-white mt-4 mb-2">Priority Highlights</h4>
            <p>Leads can be assigned priorities. High and Urgent priorities will automatically highlight the row in your tables so they catch your eye!</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
              <div className="bg-red-50 dark:bg-red-900/20 p-3 rounded-lg border border-red-100 dark:border-red-800">
                <span className="px-2 py-1 text-[10px] rounded-full bg-red-100 text-red-700 font-bold dark:bg-red-900/40 dark:text-red-300">Urgent</span>
                <span className="ml-2 text-xs font-semibold text-red-900 dark:text-red-200">Needs immediate attention!</span>
              </div>
              <div className="bg-orange-50 dark:bg-orange-900/20 p-3 rounded-lg border border-orange-100 dark:border-orange-800">
                <span className="px-2 py-1 text-[10px] rounded-full bg-orange-100 text-orange-700 font-bold dark:bg-orange-900/40 dark:text-orange-300">High</span>
                <span className="ml-2 text-xs font-semibold text-orange-900 dark:text-orange-200">Important upcoming prospect.</span>
              </div>
            </div>

            <h4 className="font-bold text-navy-700 dark:text-white mb-2">Adding Notes & Schedules</h4>
            <p>Inside a Lead's Profile, you can add <b>Notes</b> and <b>Follow-ups</b>. Setting a Follow-up Date will automatically populate your Calendar!</p>
            <div className="mt-2 p-3 bg-gray-50 dark:bg-navy-700 rounded-lg border border-gray-100 dark:border-white/10 flex flex-col gap-2">
              <div className="bg-white dark:bg-navy-800 p-2 rounded shadow-sm text-xs border-l-4 border-brand-500">
                <p className="font-bold text-navy-700 dark:text-white">Follow-up Call Scheduled</p>
                <p className="text-gray-500">Please call customer regarding new quotation package.</p>
                <p className="text-brand-500 mt-1 font-semibold">Dec 15, 2026 - 10:00 AM</p>
              </div>
            </div>
          </div>
        </AccordionItem>

        {/* CONVERT TO CLIENT */}
        <AccordionItem
          title="Converting Lead to Client"
          icon={MdSwapHoriz}
          isOpen={openSection === "convert"}
          onClick={() => toggleSection("convert")}
        >
          <div className="text-sm text-gray-600 dark:text-gray-300 space-y-4">
            <p>
              Once a Lead accepts your proposal and officially starts business with you, you should convert them into a Client.
            </p>

            <div className="p-5 border border-green-100 bg-green-50 dark:border-green-900 dark:bg-green-900/20 rounded-xl my-4 text-center">
              <div className="flex justify-center items-center gap-4">
                <div className="p-3 bg-white dark:bg-navy-800 rounded-xl shadow font-bold text-navy-700 dark:text-white flex items-center gap-2 border border-gray-200 dark:border-white/10">
                  <MdAssignment className="text-brand-500 h-5 w-5" />
                  Lead
                </div>
                <div className="text-green-500">
                  <MdSwapHoriz className="h-8 w-8 animate-pulse" />
                </div>
                <div className="p-3 bg-white dark:bg-navy-800 rounded-xl shadow font-bold text-navy-700 dark:text-white flex items-center gap-2 border border-gray-200 dark:border-white/10">
                  <MdPerson className="text-green-500 h-5 w-5" />
                  Client
                </div>
              </div>
              <p className="mt-4 text-xs font-medium text-green-800 dark:text-green-300">
                All notes, schedules, and associated files are automatically transferred over to the Client profile!
              </p>
            </div>

            <h4 className="font-bold text-navy-700 dark:text-white mt-4 mb-2">How to Convert:</h4>
            <ol className="list-decimal pl-5 space-y-2">
              <li>Open the <b>Lead Profile</b> you wish to convert.</li>
              <li>Locate the <b>Action Dropdown</b> (usually three dots on the right side of the banner).</li>
              <li>Select <b>"Convert to Client"</b>.</li>
              <li>The system will permanently move this record out of the Leads table and into the Clients table.</li>
            </ol>
          </div>
        </AccordionItem>

        {/* CLIENT MANAGEMENT */}
        <AccordionItem
          title="Client Management & Receipts"
          icon={MdPerson}
          isOpen={openSection === "client"}
          onClick={() => toggleSection("client")}
        >
          <div className="text-sm text-gray-600 dark:text-gray-300 space-y-4">
            <p>
              Clients have extra capabilities that Leads do not have, specifically around financial tracking.
            </p>
            
            <h4 className="font-bold text-navy-700 dark:text-white mt-4 mb-2">Generating Receipts</h4>
            <p>In the Client Profile, there is a dedicated <b>Receipts</b> tab. Here you can:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Create professional receipts for payments received.</li>
              <li>Track total revenue generated by this client.</li>
              <li>Print or download receipts as PDF files.</li>
            </ul>

            <div className="mt-4 p-4 border border-teal-100 bg-teal-50 dark:border-teal-900 dark:bg-teal-900/20 rounded-xl">
              <p className="font-semibold text-teal-800 dark:text-teal-300 mb-2">Visual Interface Dummy (Receipt):</p>
              <div className="flex flex-col gap-2 bg-white dark:bg-navy-800 p-4 rounded-xl shadow-sm border border-gray-200 dark:border-white/10">
                <div className="flex justify-between items-start border-b pb-3 dark:border-white/10">
                  <div className="flex gap-3">
                    <div className="bg-brand-50 text-brand-500 dark:bg-brand-400/10 dark:text-brand-400 p-2 rounded-lg">
                      <MdReceiptLong className="h-6 w-6" />
                    </div>
                    <div>
                      <h5 className="font-bold text-navy-700 dark:text-white text-sm">Receipt #RCPT-1024</h5>
                      <p className="text-xs text-gray-500">Issued: Aug 14, 2026</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <h5 className="font-bold text-teal-500 text-lg">$2,500.00</h5>
                    <span className="px-2 py-0.5 text-[10px] rounded bg-green-100 text-green-700 font-bold dark:bg-green-900/40 dark:text-green-300">Paid</span>
                  </div>
                </div>
                <div className="pt-2 flex justify-between items-center">
                  <p className="text-xs font-medium text-gray-600 dark:text-gray-300">Payment for Web Design Services</p>
                  <button className="text-xs font-bold text-brand-500 hover:text-brand-600 transition-colors">Download PDF</button>
                </div>
              </div>
            </div>

            <h4 className="font-bold text-navy-700 dark:text-white mt-6 mb-2">Reverting a Client to a Lead</h4>
            <p>If a client cancels unexpectedly or a project is paused indefinitely, you can revert them back to a Lead so they re-enter your sales pipeline.</p>
            <div className="bg-gray-50 dark:bg-navy-700 p-3 rounded-lg border border-gray-100 dark:border-white/10 flex items-center gap-3">
              <div className="bg-orange-100 text-orange-600 dark:bg-orange-900/40 dark:text-orange-300 p-2 rounded-lg">
                <MdSwapHoriz className="h-5 w-5" />
              </div>
              <p className="text-xs">Inside the Client Profile, click the dropdown and select <b>"Convert to Lead"</b>. They will vanish from Clients and reappear in Leads.</p>
            </div>
          </div>
        </AccordionItem>

        {/* STAFF MANAGEMENT */}
        <AccordionItem
          title="Staff Management & Permissions"
          icon={MdGroups}
          isOpen={openSection === "staff"}
          onClick={() => toggleSection("staff")}
        >
          <div className="text-sm text-gray-600 dark:text-gray-300 space-y-4">
            <p>
              As a Business Owner, you can invite your staff to use the CRM. You can configure precise permissions for each staff member.
            </p>

            <div className="mt-4 p-4 border border-purple-100 bg-purple-50 dark:border-purple-900 dark:bg-purple-900/20 rounded-xl">
              <p className="font-semibold text-purple-800 dark:text-purple-300 mb-2">Permission Matrix Example:</p>
              <div className="flex flex-col gap-2 bg-white dark:bg-navy-800 p-3 rounded shadow-sm text-xs border border-gray-200 dark:border-white/10">
                <div className="flex justify-between items-center border-b pb-2 dark:border-white/10">
                  <span className="font-bold text-navy-700 dark:text-white">Lead Management</span>
                  <div className="flex gap-2">
                    <span className="bg-brand-50 text-brand-500 dark:bg-brand-400/10 dark:text-brand-400 px-2 py-0.5 rounded font-bold">Read</span>
                    <span className="bg-brand-50 text-brand-500 dark:bg-brand-400/10 dark:text-brand-400 px-2 py-0.5 rounded font-bold">Create</span>
                    <span className="bg-gray-100 text-gray-400 dark:bg-gray-700 px-2 py-0.5 rounded line-through">Delete</span>
                  </div>
                </div>
                <div className="flex justify-between items-center pt-1">
                  <span className="font-bold text-navy-700 dark:text-white">Staff Management</span>
                  <div className="flex gap-2">
                    <span className="bg-red-50 text-red-500 dark:bg-red-900/20 dark:text-red-400 px-2 py-0.5 rounded font-bold">Access Denied</span>
                  </div>
                </div>
              </div>
            </div>

            <p className="mt-2">
              Staff members will only see sidebar menus and buttons that they have explicit permission to use.
            </p>
          </div>
        </AccordionItem>

        {/* FOLDERS & DOCUMENTS */}
        <AccordionItem
          title="Folders & Document Storage"
          icon={MdFolder}
          isOpen={openSection === "folders"}
          onClick={() => toggleSection("folders")}
        >
          <div className="text-sm text-gray-600 dark:text-gray-300 space-y-4">
            <p>
              The <b>Folders Content</b> section is your centralized file storage. You can create organizational folders and upload files (images, PDFs, documents) here.
            </p>
            
            <div className="flex items-center gap-3 bg-gray-50 dark:bg-navy-700 p-3 rounded-lg border border-gray-100 dark:border-white/10 mt-2">
              <div className="bg-yellow-100 text-yellow-600 dark:bg-yellow-900/40 dark:text-yellow-400 p-2 rounded-lg">
                <MdFolder className="h-6 w-6" />
              </div>
              <div>
                <h5 className="font-bold text-navy-700 dark:text-white text-sm">Organized Assets</h5>
                <p className="text-xs">Create folders like "Contracts" or "Invoices" to keep everything tidy.</p>
              </div>
            </div>
            <p className="mt-2 text-xs italic">
              Note: Files uploaded directly to a Lead or Client's profile are linked to that specific person, whereas Folders are for global business documents.
            </p>
          </div>
        </AccordionItem>

        {/* DASHBOARD ANALYTICS */}
        <AccordionItem
          title="Dashboard Analytics"
          icon={MdBarChart}
          isOpen={openSection === "dashboard"}
          onClick={() => toggleSection("dashboard")}
        >
          <div className="text-sm text-gray-600 dark:text-gray-300 space-y-4">
            <p>
              Your Main Dashboard provides a bird's-eye view of your business performance through four distinct charts:
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div className="flex items-start gap-3 bg-white dark:bg-navy-800 p-4 rounded-xl shadow-sm border border-gray-100 dark:border-white/10">
                <div className="bg-green-100 text-green-600 p-2 rounded-lg dark:bg-green-900/40 dark:text-green-400">
                  <MdAttachMoney className="h-6 w-6" />
                </div>
                <div>
                  <h5 className="font-bold text-navy-700 dark:text-white">Monthly Revenue (Line Chart)</h5>
                  <p className="text-xs mt-1">Tracks total revenue generated month-over-month. It compares the current month's revenue against the previous month, showing percentage growth.</p>
                </div>
              </div>

              <div className="flex items-start gap-3 bg-white dark:bg-navy-800 p-4 rounded-xl shadow-sm border border-gray-100 dark:border-white/10">
                <div className="bg-brand-50 text-brand-500 p-2 rounded-lg dark:bg-brand-900/40 dark:text-brand-400">
                  <MdBarChart className="h-6 w-6" />
                </div>
                <div>
                  <h5 className="font-bold text-navy-700 dark:text-white">Leads vs Clients (Bar Chart)</h5>
                  <p className="text-xs mt-1">A weekly breakdown of your pipeline. Shows the number of new leads generated versus the number of leads successfully converted to clients each week.</p>
                </div>
              </div>

              <div className="flex items-start gap-3 bg-white dark:bg-navy-800 p-4 rounded-xl shadow-sm border border-gray-100 dark:border-white/10">
                <div className="bg-blue-100 text-blue-600 p-2 rounded-lg dark:bg-blue-900/40 dark:text-blue-400">
                  <MdCalendarToday className="h-6 w-6" />
                </div>
                <div>
                  <h5 className="font-bold text-navy-700 dark:text-white">Conversion Rates (Traffic Chart)</h5>
                  <p className="text-xs mt-1">Displays your success rate. It calculates the percentage of leads that successfully turned into paying clients over a specific timeframe.</p>
                </div>
              </div>

              <div className="flex items-start gap-3 bg-white dark:bg-navy-800 p-4 rounded-xl shadow-sm border border-gray-100 dark:border-white/10">
                <div className="bg-orange-100 text-orange-600 p-2 rounded-lg dark:bg-orange-900/40 dark:text-orange-400">
                  <MdInfoOutline className="h-6 w-6" />
                </div>
                <div>
                  <h5 className="font-bold text-navy-700 dark:text-white">Lead Sources (Pie Chart)</h5>
                  <p className="text-xs mt-1">A visual distribution of where your business comes from (e.g., Facebook, Referrals, Cold Calls) so you know which marketing channels are working best.</p>
                </div>
              </div>
            </div>
          </div>
        </AccordionItem>

        {/* SYSTEM CONFIGURATION */}
        <AccordionItem
          title="System Configuration"
          icon={MdSettings}
          isOpen={openSection === "config"}
          onClick={() => toggleSection("config")}
        >
          <div className="text-sm text-gray-600 dark:text-gray-300 space-y-4">
            <p>
              The <b>Configuration</b> menu allows the business owner to define the dropdown options used throughout the CRM.
            </p>
            <ul className="list-disc pl-5 space-y-2 mt-2">
              <li><b>Statuses:</b> Define the stages of your pipeline (e.g., "New", "In Progress", "Lost").</li>
              <li><b>Sources:</b> Track where leads come from (e.g., "Facebook", "Referral", "Cold Call").</li>
              <li><b>Locations:</b> Add Countries, States, and Districts for standardized address entry.</li>
            </ul>
            <div className="bg-yellow-50 dark:bg-yellow-900/20 p-3 rounded-lg border border-yellow-100 dark:border-yellow-800 mt-4">
              <p className="text-xs font-semibold text-yellow-900 dark:text-yellow-200">
                Tip: Standardizing these lists prevents typos and makes filtering your tables much more accurate!
              </p>
            </div>
          </div>
        </AccordionItem>

      </div>
    </div>
  );
};

export default UserManual;
